/**
 * Ruilen.
 *
 * Twee vormen, met een duidelijke rangorde in het spel: eerst probeer je te ruilen met
 * je tegenstander (vrij onderhandelen), en lukt dat niet, dan met de bank tegen een
 * vaste koers (handboek p. 15-16).
 *
 * De bankkoers is standaard **3:1**. Een handelsvloot voor een bepaalde grondstof maakt
 * daar **2:1** van, en een vuurtoren naast een vloot zelfs **1:1** (kennisbank 02 §2.2).
 * De koers geldt per grondstofsoort die je wég geeft.
 */
import type { Resource } from '@catan/contracts';
import type { PlayerState } from './state.js';
import { resourcePool } from './building.js';

/** Standaardkoers met de bank: drie gelijke grondstoffen voor één naar keuze. */
export const DEFAULT_BANK_RATE = 3;

export interface TradeAbilities {
  /** Grondstoffen waarvoor deze speler een handelsvloot heeft (koers 2:1). */
  readonly fleets: ReadonlySet<Resource>;
  /** Grondstoffen waarvoor de koers 1:1 is (vuurtoren naast de vloot, munterij voor goud). */
  readonly direct: ReadonlySet<Resource>;
}

export const NO_ABILITIES: TradeAbilities = { fleets: new Set(), direct: new Set() };

/** Hoeveel exemplaren van `resource` kost één grondstof naar keuze bij de bank? */
export function bankRate(resource: Resource, abilities: TradeAbilities): number {
  if (abilities.direct.has(resource)) return 1;
  if (abilities.fleets.has(resource)) return 2;
  return DEFAULT_BANK_RATE;
}

/** Welke grondstoffen kan deze speler nu bij de bank inruilen? */
export function tradableWithBank(
  player: PlayerState,
  abilities: TradeAbilities,
): { resource: Resource; rate: number }[] {
  const pool = resourcePool(player);
  return (Object.entries(pool) as [Resource, number][])
    .map(([resource, amount]) => ({ resource, rate: bankRate(resource, abilities), amount }))
    .filter((entry) => entry.amount >= entry.rate)
    .map(({ resource, rate }) => ({ resource, rate }));
}

// ---------------------------------------------------------------- spelerruil

/** Een ruilaanbod: wat je geeft en wat je ervoor wilt. */
export interface TradeOffer {
  readonly give: Readonly<Partial<Record<Resource, number>>>;
  readonly want: Readonly<Partial<Record<Resource, number>>>;
}

export type OfferStatus = 'open' | 'geaccepteerd' | 'afgewezen' | 'ingetrokken';

export interface PendingOffer extends TradeOffer {
  /** Wie het aanbod deed. */
  readonly from: 0 | 1;
  readonly status: OfferStatus;
}

function totalOf(bundle: Readonly<Partial<Record<Resource, number>>>): number {
  return Object.values(bundle).reduce<number>((sum, amount) => sum + (amount ?? 0), 0);
}

/** Kan deze speler leveren wat hij aanbiedt? */
export function canOffer(player: PlayerState, offer: TradeOffer): boolean {
  const pool = resourcePool(player);
  for (const [resource, amount] of Object.entries(offer.give) as [Resource, number][]) {
    if (pool[resource] < amount) return false;
  }
  return true;
}

/** Kan de tegenpartij leveren wat er gevraagd wordt? */
export function canFulfil(player: PlayerState, offer: TradeOffer): boolean {
  const pool = resourcePool(player);
  for (const [resource, amount] of Object.entries(offer.want) as [Resource, number][]) {
    if (pool[resource] < amount) return false;
  }
  return true;
}

/** Is dit een zinnig aanbod? Leeg of eenzijdig leeg mag niet. */
export function isSensibleOffer(offer: TradeOffer): { ok: true } | { ok: false; reason: string } {
  if (totalOf(offer.give) === 0) return { ok: false, reason: 'je biedt niets aan' };
  if (totalOf(offer.want) === 0) return { ok: false, reason: 'je vraagt niets terug' };
  for (const bundle of [offer.give, offer.want]) {
    for (const [resource, amount] of Object.entries(bundle) as [Resource, number][]) {
      if (!Number.isInteger(amount) || amount <= 0) {
        return { ok: false, reason: `ongeldig aantal voor ${resource}` };
      }
    }
  }
  // Dezelfde grondstof geven én vragen is zinloos.
  for (const resource of Object.keys(offer.give) as Resource[]) {
    if (offer.want[resource] !== undefined) {
      return { ok: false, reason: `je biedt en vraagt allebei ${resource}` };
    }
  }
  return { ok: true };
}
